// AuthFunctions.js
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  onAuthStateChanged,
} from "firebase/auth";
import { doc, setDoc, getDoc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase";

// Register a new user
// export const registerUser = async (email, password) => {
//   try {
//     const userCredential = await createUserWithEmailAndPassword(
//       auth,
//       email,
//       password
//     );
//     const user = userCredential.user;
//     return user;
//   } catch (error) {
//     console.error("Error registering user:", error);
//     throw error;
//   }
// };

// Register a new user and store additional details in Firestore
export const registerUser = async (email, password, additionalData) => {
  try {
    // Create user in Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Store additional user details in Firestore
    const userDocRef = doc(db, "users", user.uid); // 'users' is the collection name, use your own collection name
    await setDoc(userDocRef, additionalData);

    return user;
  } catch (error) {
    console.error("Error registering user:", error);
    throw error;
  }
};

// Sign in with email and password
export const signInUser = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Retrieve additional user details from Firestore
    const userDetails = await getUserDetails(user.uid);

    return { ...user, ...userDetails };
  } catch (error) {
    console.error("Error signing in:", error);
    throw error;
  }
};

// Register a new user and automatically sign them in
export const registerUserAndSignIn = async (
  email,
  password,
  additionalData
) => {
  try {
    // Create user in Firebase Authentication
    const userCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    // Store additional user details in Firestore
    const userDocRef = doc(db, "users", user.uid); // 'users' is the collection name, use your own collection name
    await setDoc(userDocRef, additionalData);

    // Automatically sign in the user
    await onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in
        console.log("User signed in immediately after registration:", user);
      } else {
        // No user is signed in
        console.error("User not signed in after registration.");
      }
    });

    return user;
  } catch (error) {
    console.error("Error registering user and signing in:", error);
    throw error;
  }
};

// Get additional user details from Firestore
export const getUserDetails = async (uid) => {
  try {
    const userDocRef = doc(db, "users", uid); // 'users' is the collection name, use your own collection name
    const userDocSnap = await getDoc(userDocRef);

    if (userDocSnap.exists()) {
      return userDocSnap.data();
    } else {
      console.log("No user details found in Firestore for UID:", uid);
      return {};
    }
  } catch (error) {
    console.error("Error getting user details:", error);
    throw error;
  }
};

// Update the password for the current user
export const updatePasswordForCurrentUser = async (
  currentPassword,
  newPassword
) => {
  try {
    const user = auth.currentUser;

    if (user) {
      // Reauthenticate the user first
      const credentials = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );
      await reauthenticateWithCredential(user, credentials);

      // Update the password
      await updatePassword(user, newPassword);

      console.log("Password updated successfully");
    } else {
      console.error("No user is currently signed in.");
    }
  } catch (error) {
    console.error("Error updating password:", error);
    throw error;
  }
};

// Update address, city, state, and zipcode for the current user
export const updateAddressDetailsForCurrentUser = async (
  address,
  city,
  state,
  zipcode
) => {
  try {
    const user = auth.currentUser;

    if (user) {
      const userDocRef = doc(db, "users", user.uid); // 'users' is the collection name, use your own collection name
      await updateDoc(userDocRef, { address, city, state, zipcode });
      console.log("Address details updated successfully");
    } else {
      console.error("No user is currently signed in.");
    }
  } catch (error) {
    console.error("Error updating address details:", error);
    throw error;
  }
};

// Sign out the current user
export const signOutUser = async () => {
  try {
    await signOut(auth);
    console.log("User signed out");
  } catch (error) {
    console.error("Error signing out:", error);
    throw error;
  }
};
